import bigDecimal from 'js-big-decimal';
import { formatNepaliNumber } from "@/ts/NepaliAmountPattern";

export type DisplayFormat = 'accounting' | 'currency';

function toFixedSafe(val: number | string | null | undefined, decimals: number): string {
    if (val === null || val === undefined || val === '') return '';
    if (isNaN(Number(val))) return String(val);
    const bd = new bigDecimal(val);
    return bd.abs().round(decimals, bigDecimal.RoundingModes.HALF_UP).getValue();
}

function toRangeSafe(val: number | string | null | undefined, minDecimals: number, maxDecimals: number): string {
    if (val === null || val === undefined || val === '') return '';
    if (isNaN(Number(val))) return String(val);
    const bd = new bigDecimal(val);
    const rounded = bd.abs().round(maxDecimals, bigDecimal.RoundingModes.HALF_UP).getValue();
    const [intPart, decPart = ''] = rounded.split('.');
    let trimmedDec = decPart.replace(/0+$/, '');
    if (trimmedDec.length < minDecimals) trimmedDec = trimmedDec.padEnd(minDecimals, '0');
    return trimmedDec.length > 0 ? `${intPart}.${trimmedDec}` : intPart;
}

export interface FormatAmountOptions {
    decimals?: number;
    minDecimals?: number;
    maxDecimals?: number;
    displayFormat?: DisplayFormat;
    displayCurrency?: boolean;
    currency?: string;
    grouping?: boolean;
}

export function formatAmountDisplay(
    val: number | string | null | undefined,
    options: FormatAmountOptions = {}
): string {
    const {
        decimals = 2,
        minDecimals = decimals,
        maxDecimals = decimals,
        displayFormat = 'currency',
        displayCurrency = false,
        currency = 'Rs.',
        grouping = true,
    } = options;

    if (val === null || val === undefined || val === '') return '';
    if (isNaN(Number(val))) return String(val);

    const numeric = Number(val);
    const isNegative = numeric < 0;

    const rawFormatted = toRangeSafe(val, minDecimals, maxDecimals);
    const absoluteFormatted = formatNepaliNumber(rawFormatted, grouping);

    const core = isNegative
        ? (displayFormat === 'accounting' ? `(${absoluteFormatted})` : `-${absoluteFormatted}`)
        : absoluteFormatted;

    return displayCurrency ? `${currency} ${core}` : core;
}

export function formatAmountTooltip(
    val: number | string | null | undefined,
    options: FormatAmountOptions = {}
): string | undefined {
    const { decimals = 2, displayFormat = 'currency' } = options;
    if (decimals >= 6 || val === null || val === undefined || val === '' || isNaN(Number(val))) return undefined;

    const numeric = Number(val);
    const isZero = numeric === 0;
    const isNegative = numeric < 0;
    const absoluteFullPrecision = formatNepaliNumber(toRangeSafe(val, decimals, 6));

    if (isZero && displayFormat === 'accounting') return '0.00';
    if (isNegative) return displayFormat === 'accounting' ? `(${absoluteFullPrecision})` : `-${absoluteFullPrecision}`;
    return absoluteFullPrecision;
}
